import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  updateName,
  dashboardSelectors,
  resetCurrentDashboard,
} from 'state/normalized/dashboard/dashboardSlice';
import isEmpty from 'lodash/isEmpty';
import intl from 'react-intl-universal';
import { FormItem, TextField } from '@getsynapse/design-system';
import { MEDIUM_INPUTS_LENGTH } from 'utils/constants';
import useModal from 'Hooks/useModal';
import { useAppThunkDispatch } from 'state/store';

const EditDashboardNameModal: React.FC<{
  isOpen: boolean;
  onCloseModal: () => void;
  afterEditDashboard?: (dashboardId: string) => void;
}> = ({ isOpen = false, onCloseModal, afterEditDashboard }) => {
  const { Modal, modalProps, openModal, closeModal } = useModal();
  const dispatch = useAppThunkDispatch();
  const [name, setName] = useState<string>('');
  const currentDashboardData = useSelector(
    dashboardSelectors.selectCurrentDashboard
  );

  useEffect(() => {
    setName(currentDashboardData?.name ?? '');
  }, [currentDashboardData]);

  useEffect(() => {
    if (isOpen && !modalProps.isOpen) {
      openModal(modalProps);
    }
  }, [openModal, modalProps, isOpen]);

  const handleCloseModal = useCallback(() => {
    dispatch(resetCurrentDashboard());
    onCloseModal();
    closeModal();
  }, [closeModal, dispatch, onCloseModal]);

  const canSave = useMemo(() => {
    return !isEmpty(name) && currentDashboardData?.name;
  }, [name, currentDashboardData]);

  const handleSave = useCallback(
    async (name: string) => {
      if (currentDashboardData) {
        await dispatch(
          updateName({
            updateParams: {
              dashboardId: currentDashboardData.id,
              update: { name: name },
              token: '',
            },
          })
        );
        afterEditDashboard && afterEditDashboard(currentDashboardData.id);
        handleCloseModal();
      }
    },
    [afterEditDashboard, currentDashboardData, dispatch, handleCloseModal]
  );

  const actionButtons = useMemo(
    () => [
      {
        children: intl.get('EDIT_DASHBOARD_MODAL.SAVE_CHANGES'),
        variant: 'primary',
        className: 'h-10',
        disabled: !canSave,
        loading: false,
        onClick: () => handleSave(name),
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        className: 'h-10',
      },
    ],
    [canSave, handleCloseModal, handleSave, name]
  );

  return (
    <Modal
      {...modalProps}
      aria-label={intl.get('EDIT_DASHBOARD_MODAL.TITLE')}
      title={intl.get('EDIT_DASHBOARD_MODAL.TITLE')}
      titleIcon={{
        name: 'pencil-outline',
        className: 'text-primary-dark',
      }}
      size='medium'
      actionButtons={actionButtons}
      closeModal={handleCloseModal}
    >
      <FormItem>
        <TextField
          label={intl.get('CREATE_DASHBOARD_MODAL.DASHBOARD_NAME')}
          labelProps={{
            required: true,
          }}
          displayCharactersCount
          maxLength={MEDIUM_INPUTS_LENGTH}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          placeholder={intl.get(
            'CREATE_DASHBOARD_MODAL.DASHBOARD_NAME_PLACEHOLDER'
          )}
          className='w-full'
          data-cy='dashboard-input_name'
        />
      </FormItem>
    </Modal>
  );
};

export default EditDashboardNameModal;
